import React from "react";
import PropTypes from "prop-types";
import { useFormContext, Controller } from "react-hook-form";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Box, FormHelperText } from "@mui/material";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";

import "./DatePicker.scss";

function CustomDatePicker({
  name,
  label,
  helperText,
  sx,
  placeholder,
  value: propValue,
  onChange: propOnChange,
  error: propError,
  size = "small",
  variant = "outlined",
  disabled,
  width,
  monthYearOnly = false,
  minDate = null,
  maxDate = null,
  ...other
}) {
  const formContext = useFormContext();
  const isControlled = propValue !== undefined && propOnChange !== undefined;

  // Remount-key gating (see the <DatePicker> comment below): the MUI v9 sectioned field is
  // kept UNCONTROLLED (defaultValue) so it self-manages mid-entry typing without wiping
  // sibling sections. To still reflect EXTERNAL value changes (form reset, programmatic set,
  // reopening an edit modal for a different record) we remount via `key` - but only when the
  // incoming value differs from what THIS field last emitted, so the field's own self-commits
  // never remount it (a remount mid-edit would drop section focus).
  const lastCommittedRef = React.useRef(null);
  const remountKeyRef = React.useRef(0);

  // API/Redux data commonly stores dates as ISO strings. The underlying MUI DatePicker
  // (backed by AdapterDateFns) only recognizes a real `Date` instance as a valid value -
  // a raw string fails `date-fns`'s `isValid` check and renders as an empty field even
  // though a real value exists. Normalize to a `Date` (or null) without altering values
  // that are already `Date` instances.
  const toDateOrNull = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const handleChange = (date, fieldOnChange, context) => {
    if (date === null) {
      lastCommittedRef.current = null;
      fieldOnChange(null);
      if (propOnChange) {
        propOnChange(null);
      }
      return;
    }

    // MUI v9's sectioned field (unlike the old masked-input DatePicker) fires onChange
    // after every completed section, even while sibling sections are still unset - e.g.
    // typing the month of a MM/yyyy field fires onChange with a placeholder/sentinel year
    // before the user has typed the year at all. `context.validationError` flags most of
    // these incomplete/out-of-range values (e.g. a sentinel year MUI defaults to while the
    // year section is still empty), but a partially-typed year (e.g. "202" while the user
    // is still typing "2024") can momentarily fall within range and report no error, so we
    // also require a genuine 4-digit year before treating the value as complete. Previously
    // incomplete values were forwarded upstream as the literal string "Invalid Date", which
    // broke consumers that fed it straight into date-fns `format` (which throws on invalid
    // dates) - aborting their state update and making the field appear to lose the value
    // the user had just finished selecting. Ignore incomplete/invalid values entirely
    // instead; only propagate a real, complete, in-range date.
    const year = date?.getFullYear?.();
    const isCompleteYear = year !== undefined && !Number.isNaN(year) && Math.abs(year).toString().length === 4;

    if (context?.validationError || !isCompleteYear) {
      return;
    }

    lastCommittedRef.current = date;
    fieldOnChange(date);
    if (propOnChange) {
      propOnChange(date);
    }
  };

  const renderTextField = (field = {}, fieldError) => {
    const value = isControlled ? toDateOrNull(propValue) : toDateOrNull(field.value);

    // Bump the remount key only when `value` changed to something this field did NOT emit
    // (an external/programmatic change or clear), never on our own self-commits.
    const valueTime = value ? value.getTime() : null;
    const committedTime = lastCommittedRef.current ? lastCommittedRef.current.getTime() : null;
    if (valueTime !== committedTime) {
      remountKeyRef.current += 1;
      lastCommittedRef.current = value;
    }

    return (
      <div
        className={`w-100p date-picker-container ${propError || fieldError ? "has-error" : ""} ${disabled ? "is-disabled" : ""} ${!value ? "is-empty" : ""}`}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box shrink htmlFor={name}>
            {label}
          </Box>
          <DatePicker
            {...other}
            // Uncontrolled field (defaultValue) so MUI v9's sectioned field self-manages
            // mid-entry typing without wiping sibling sections. A controlled `value` that
            // only advances once a full date is complete makes MUI resync half-typed
            // sections back to the stale value - e.g. typing "08" then "2024" into an empty
            // MM/yyyy field ends up as "MM/2024". External value changes (form reset,
            // programmatic set, reopening an edit modal) are reflected via the gated
            // `remountKeyRef` key above, which bumps ONLY on non-self-emitted value changes
            // so it never remounts (and drops focus) during an in-place section edit. The
            // `handleChange` guard still refuses to propagate incomplete/invalid dates, so
            // consumers never receive the "Invalid Date" that used to throw in date-fns.
            key={remountKeyRef.current}
            defaultValue={value}
            disabled={disabled}
            onChange={(date, context) => {
              handleChange(date, field?.onChange, context);
            }}
            format={monthYearOnly ? "MM/yyyy" : "MMM dd, yyyy"}
            localeText={{
              // MUI v9 sectioned fields ignore the input `placeholder` attribute and
              // auto-generate per-section placeholders; its default month placeholder is
              // "MMMM" for any letter-format month. Force "MMM" to match QA (and the
              // `MMM dd, yyyy` format). Digit months (monthYearOnly's MM/yyyy) stay "MM".
              fieldMonthPlaceholder: (params) => (params.contentType === "letter" ? "MMM" : "MM"),
            }}
            minDate={minDate}
            maxDate={maxDate}
            views={monthYearOnly ? ["year", "month"] : ["year", "month", "day"]}
            slots={{
              openPickerIcon: InsertInvitationIcon,
            }}
            slotProps={{
              textField: {
                size,
                variant,
                error: !!fieldError,
                placeholder: placeholder || (monthYearOnly ? "MM/YYYY" : "MMM DD, YYYY"),
                sx: { width, ...sx },
              },
              dialog: { className: "date-picker-container" },
              popper: {
                placement: "bottom-start",
                className: "date-picker-container",
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [-20],
                    },
                  },
                ],
              },
            }}
          />
          {fieldError && (
            <FormHelperText className="custom-helper-text">{fieldError.message || helperText}</FormHelperText>
          )}
        </LocalizationProvider>
      </div>
    );
  };

  if (formContext && !isControlled) {
    return (
      <Controller
        name={name}
        control={formContext.control}
        render={({ field, fieldState: { error } }) => renderTextField(field, error)}
      />
    );
  }

  return renderTextField({ value: propValue, onChange: propOnChange }, propError ? { message: helperText } : null);
}

CustomDatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  sx: PropTypes.shape({}),
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
  onChange: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  size: PropTypes.string,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  monthYearOnly: PropTypes.bool,
  minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
  maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
};

CustomDatePicker.defaultProps = {
  helperText: "",
  sx: {},
  placeholder: "",
  value: undefined,
  onChange: undefined,
  error: false,
  size: "small",
  disabled: false,
  variant: "outlined",
  monthYearOnly: false,
  minDate: null,
  maxDate: null,
};

export default CustomDatePicker;
